/**
 * Product card colour swatches: clicking a swatch swaps the card to that colour's
 * product in place instead of navigating to its product page.
 *
 * Markup contract (snippets/product-loop-item.liquid, sections/v1-media-products.liquid,
 * snippets/product-card-swatches.liquid):
 *   [data-product-card]                       card root
 *   [data-card-swatch][data-card-swatch-url]  swatch link (href stays as the no-JS fallback)
 *   [data-card-slot="<name>"]                 region whose content is replaced
 *   [data-card-image="primary|secondary"]     images re-pointed to the new product
 *   [data-card-image-availability]            image that carries the sold-out state
 *   [data-card-link]                          links re-pointed to the new product
 *   [data-card-quick-add]                     quick-add button (handle / single variant id)
 *
 * Fragments come from templates/product.card-swap.liquid (?view=card-swap).
 *
 * Speed: a swatch's card data (small HTML, no images) is prefetched when it is hovered,
 * focused or pressed, and kept per page view. On click the chosen swatch highlights at once,
 * the text / price / sizes switch as soon as the data arrives, and the photo switches once
 * the new one has loaded (the old one stays until then, so there is never a blank image).
 *
 * Ctrl/Cmd/Shift/middle clicks keep the normal link behaviour (open in new tab etc.).
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[product-card-swatch-swap]';
  var SWAP_VIEW = 'card-swap';
  var PRODUCT_PATH_PATTERN = /\/products\/[^/?#]+/;
  var IMAGE_WIDTH_PLACEHOLDER = '{width}';
  var DEFAULT_IMAGE_WIDTHS = [360, 540, 720, 900, 1080];
  // Sharp on high-density screens without reading window.devicePixelRatio.
  var TARGET_PIXEL_RATIO = 2;

  var SELECTORS = {
    card: '[data-product-card]',
    swatch: '[data-card-swatch]',
    slot: '[data-card-slot]',
    image: '[data-card-image]',
    primaryImage: '[data-card-image="primary"]',
    link: '[data-card-link]',
    quickAdd: '[data-card-quick-add]',
    swapSource: '[data-card-swap-source]',
    slotSource: 'template[data-card-slot-source]',
    imageSource: '[data-card-image-source]'
  };

  var STATE_CLASSES = {
    swapping: 'is-swapping',
    activeSwatch: 'mp__swatch--active',
    unavailableImage: 'unavailable',
    lazyLoaded: 'lazyloaded',
    lazyUnhandled: ['lazyload', 'lazyloading']
  };

  var PENDING_SWAP_ATTRIBUTE = 'data-card-swap-pending';
  // Latest swap applied to a card; lets late image loads from an older click be ignored.
  // A touch moving further than this (px) is a scroll, not a tap.
  var TAP_MOVE_TOLERANCE_PX = 10;
  var CURRENT_SWAP_ATTRIBUTE = 'data-card-swap-current';

  /* ---------- Business logic (pure, no DOM) ---------- */

  function getIsPlainPrimaryClick(clickInfo) {
    return clickInfo.button === 0 &&
      !clickInfo.metaKey && !clickInfo.ctrlKey && !clickInfo.shiftKey && !clickInfo.altKey;
  }

  function getSwapDataUrl(productUrl, baseUrl) {
    var url = new URL(productUrl, baseUrl);
    url.search = '';
    url.hash = '';
    url.searchParams.set('view', SWAP_VIEW);
    return url.toString();
  }

  // Keeps any collection / locale prefix of the existing link; the query (e.g. ?variant=)
  // belongs to the old product, so it is dropped.
  function getUpdatedProductHref(currentHref, productHandle, productUrl) {
    if (!currentHref || !PRODUCT_PATH_PATTERN.test(currentHref)) return productUrl;
    var currentPath = currentHref.split(/[?#]/)[0];
    return currentPath.replace(PRODUCT_PATH_PATTERN, '/products/' + productHandle);
  }

  // A product without a second image reuses its first one for the hover image.
  function getImageSourceForRole(imageSources, role) {
    if (role === 'secondary') return imageSources.secondary || imageSources.primary || null;
    return imageSources.primary || null;
  }

  // Smallest available width covering the displayed size; the largest one otherwise.
  function getImageWidthForDisplay(availableWidths, displayWidth, pixelRatio) {
    var widths = (availableWidths && availableWidths.length ? availableWidths : DEFAULT_IMAGE_WIDTHS)
      .slice()
      .sort(function (a, b) { return a - b; });
    var neededWidth = Math.max(displayWidth, 1) * pixelRatio;
    for (var index = 0; index < widths.length; index++) {
      if (widths[index] >= neededWidth) return widths[index];
    }
    return widths[widths.length - 1];
  }

  function getSizedImageUrl(imageUrlTemplate, width) {
    return imageUrlTemplate.split(IMAGE_WIDTH_PLACEHOLDER).join(String(width));
  }

  function parseImageWidths(widthsAttribute) {
    try {
      var widths = JSON.parse(widthsAttribute || '[]');
      return Array.isArray(widths) ? widths.filter(function (width) { return typeof width === 'number'; }) : [];
    } catch (error) {
      console.warn(LOG_PREFIX, 'Unreadable data-widths; using defaults.', widthsAttribute);
      return [];
    }
  }

  /* ---------- Data loading ---------- */

  function readImageSource(sourceElement) {
    return {
      src: sourceElement.getAttribute('data-src') || '',
      aspectRatio: sourceElement.getAttribute('data-aspectratio') || '',
      alt: sourceElement.getAttribute('data-alt') || ''
    };
  }

  function readSwapData(fragmentDocument) {
    var source = fragmentDocument.querySelector(SELECTORS.swapSource);
    if (!source) return null;

    var slots = {};
    Array.prototype.forEach.call(source.querySelectorAll(SELECTORS.slotSource), function (template) {
      slots[template.getAttribute('data-card-slot-source')] = template.innerHTML;
    });

    var images = {};
    Array.prototype.forEach.call(source.querySelectorAll(SELECTORS.imageSource), function (imageElement) {
      images[imageElement.getAttribute('data-card-image-source')] = readImageSource(imageElement);
    });

    return {
      handle: source.getAttribute('data-product-handle') || '',
      url: source.getAttribute('data-product-url') || '',
      title: source.getAttribute('data-product-title') || '',
      singleVariantId: source.getAttribute('data-single-variant-id') || '',
      isSoldOut: source.getAttribute('data-is-sold-out') === 'true',
      slots: slots,
      images: images
    };
  }

  function fetchSwapData(swapDataUrl) {
    return fetch(swapDataUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Swap data request failed with status ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var swapData = readSwapData(new DOMParser().parseFromString(html, 'text/html'));
        if (!swapData || !swapData.handle) throw new Error('Swap data missing from ' + swapDataUrl);
        return swapData;
      });
  }

  // One request per colour per page view; failed requests are forgotten so they can be retried.
  function createSwapDataCache() {
    var requestsByUrl = new Map();
    return {
      get: function (swapDataUrl) {
        if (!requestsByUrl.has(swapDataUrl)) {
          var request = fetchSwapData(swapDataUrl).catch(function (error) {
            requestsByUrl.delete(swapDataUrl);
            throw error;
          });
          requestsByUrl.set(swapDataUrl, request);
        }
        return requestsByUrl.get(swapDataUrl);
      }
    };
  }

  // Resolves once the image is ready to paint; never rejects, so a slow or broken
  // image cannot block the swap.
  function preloadImage(imageUrl) {
    return new Promise(function (resolve) {
      var image = new Image();
      image.onload = function () {
        if (typeof image.decode === 'function') {
          image.decode().then(resolve, resolve);
        } else {
          resolve();
        }
      };
      image.onerror = function () {
        console.warn(LOG_PREFIX, 'Image preload failed; swapping anyway.', imageUrl);
        resolve();
      };
      image.src = imageUrl;
    });
  }

  /* ---------- DOM: read ---------- */

  function getSwatchFromEvent(event) {
    var target = event.target;
    return target && target.closest ? target.closest(SELECTORS.swatch) : null;
  }

  function getCardImageWidth(card, image) {
    var primaryImage = card.querySelector(SELECTORS.primaryImage);
    var displayWidth = (image && image.clientWidth) || (primaryImage && primaryImage.clientWidth) || 0;
    var widths = parseImageWidths((image || primaryImage || card).getAttribute('data-widths'));
    return getImageWidthForDisplay(widths, displayWidth, TARGET_PIXEL_RATIO);
  }

  function getPrimaryImageUrl(card, swapData) {
    var imageSource = getImageSourceForRole(swapData.images, 'primary');
    if (!imageSource || !imageSource.src) return '';
    return getSizedImageUrl(imageSource.src, getCardImageWidth(card, card.querySelector(SELECTORS.primaryImage)));
  }

  /* ---------- DOM: apply ---------- */

  function applySlots(card, slots) {
    Array.prototype.forEach.call(card.querySelectorAll(SELECTORS.slot), function (slotElement) {
      var slotName = slotElement.getAttribute('data-card-slot');
      if (Object.prototype.hasOwnProperty.call(slots, slotName)) {
        slotElement.innerHTML = slots[slotName];
      }
    });
  }

  // Points the image straight at a sized URL and marks it as handled so lazysizes leaves it alone.
  function applyImageSource(card, image, imageSource, isSoldOut) {
    image.removeAttribute('srcset');
    image.setAttribute('data-src', imageSource.src);
    image.setAttribute('data-aspectratio', imageSource.aspectRatio);
    image.setAttribute('alt', imageSource.alt);
    image.setAttribute('src', getSizedImageUrl(imageSource.src, getCardImageWidth(card, image)));
    STATE_CLASSES.lazyUnhandled.forEach(function (className) { image.classList.remove(className); });
    image.classList.add(STATE_CLASSES.lazyLoaded);
    if (image.hasAttribute('data-card-image-availability')) {
      image.classList.toggle(STATE_CLASSES.unavailableImage, isSoldOut);
    }
  }

  function applyImages(card, swapData) {
    Array.prototype.forEach.call(card.querySelectorAll(SELECTORS.image), function (image) {
      var imageSource = getImageSourceForRole(swapData.images, image.getAttribute('data-card-image'));
      if (!imageSource || !imageSource.src) return;
      applyImageSource(card, image, imageSource, swapData.isSoldOut);
    });
  }

  function applyLinks(card, swapData) {
    Array.prototype.forEach.call(card.querySelectorAll(SELECTORS.link), function (link) {
      link.setAttribute('href', getUpdatedProductHref(link.getAttribute('href'), swapData.handle, swapData.url));
    });
  }

  function applyQuickAdd(card, swapData) {
    Array.prototype.forEach.call(card.querySelectorAll(SELECTORS.quickAdd), function (button) {
      button.setAttribute('data-product-handle', swapData.handle);
      button.setAttribute('aria-label', 'Quick add ' + swapData.title);
      if (swapData.singleVariantId) {
        button.setAttribute('data-variant-id', swapData.singleVariantId);
      } else {
        button.removeAttribute('data-variant-id');
      }
    });
  }

  function applyActiveSwatch(card, activeSwatch) {
    Array.prototype.forEach.call(card.querySelectorAll(SELECTORS.swatch), function (swatch) {
      var isActive = swatch === activeSwatch;
      swatch.classList.toggle(STATE_CLASSES.activeSwatch, isActive);
      if (isActive) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });
  }

  // Prices arrive in the shop currency (span.money). The BUCKS currency converter app only
  // converts prices present at page load, so it is asked to convert the new ones — the same
  // entry point complete-the-look.js and cart-drawer.js use. The app is only reachable
  // through its window global (exception to the no-window rule, pending approval). Without the app
  // the shop-currency price is already correct, so nothing is needed.
  function refreshConvertedPrices() {
    try {
      var currencyConverter = window.bucksCC;
      if (!currencyConverter) return;
      if (typeof currencyConverter.reConvert === 'function') {
        currencyConverter.reConvert();
      } else if (typeof currencyConverter.rerender === 'function') {
        currencyConverter.rerender();
      } else {
        console.warn(LOG_PREFIX, 'Currency converter has no reConvert/rerender; prices stay in shop currency.');
      }
    } catch (error) {
      console.error(LOG_PREFIX, 'Currency conversion of swapped prices failed.', error);
    }
  }

  // Everything except the images, which follow once the new photo has loaded.
  function applySwapDetails(card, swatch, swapData) {
    applySlots(card, swapData.slots);
    refreshConvertedPrices();
    applyLinks(card, swapData);
    applyQuickAdd(card, swapData);
    applyActiveSwatch(card, swatch);
  }

  function setCardSwapping(card, swapDataUrl) {
    card.setAttribute(PENDING_SWAP_ATTRIBUTE, swapDataUrl);
    card.setAttribute('aria-busy', 'true');
    card.classList.add(STATE_CLASSES.swapping);
  }

  function clearCardSwapping(card) {
    card.removeAttribute(PENDING_SWAP_ATTRIBUTE);
    card.removeAttribute('aria-busy');
    card.classList.remove(STATE_CLASSES.swapping);
  }

  /* ---------- Orchestration ---------- */

  function getSwapTarget(swatch) {
    var card = swatch.closest(SELECTORS.card);
    var swatchUrl = swatch.getAttribute('data-card-swatch-url');
    if (!card || !swatchUrl) return null;
    try {
      return { card: card, swapDataUrl: getSwapDataUrl(swatchUrl, document.baseURI) };
    } catch (error) {
      console.error(LOG_PREFIX, 'Invalid swatch product URL.', swatchUrl, error);
      return null;
    }
  }

  // The old photo stays visible until the new one is ready, so the card never shows a blank image.
  function swapImagesWhenReady(card, swapDataUrl, swapData) {
    var primaryImageUrl = getPrimaryImageUrl(card, swapData);
    var imageReady = primaryImageUrl ? preloadImage(primaryImageUrl) : Promise.resolve();
    return imageReady.then(function () {
      // A later swatch click on the same card wins.
      if (card.getAttribute(CURRENT_SWAP_ATTRIBUTE) !== swapDataUrl) return;
      applyImages(card, swapData);
      // Lets other card features (e.g. the hover image slider) refresh for the new product
      card.dispatchEvent(new CustomEvent('product-card:swapped', { bubbles: true }));
    });
  }

  // Hover / focus / tap-down prefetch: only the small card data for that one colour,
  // never images, so moving across a grid stays cheap.
  function prefetchSwatch(swapDataCache, swatch) {
    if (swatch.getAttribute('aria-current') === 'true') return;
    var swapTarget = getSwapTarget(swatch);
    if (!swapTarget) return;
    swapDataCache.get(swapTarget.swapDataUrl).catch(function (error) {
      console.warn(LOG_PREFIX, 'Prefetch failed; will retry on click.', error);
    });
  }

  // Phones have no hover: start loading the moment a finger presses a swatch.
  function handleSwatchPress(swapDataCache, event) {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    var swatch = getSwatchFromEvent(event);
    if (swatch) prefetchSwatch(swapDataCache, swatch);
  }

  function handleSwatchIntent(swapDataCache, event) {
    // Phones have no hover; touch is handled on press instead.
    if (event.pointerType === 'touch') return;
    var swatch = getSwatchFromEvent(event);
    if (!swatch) return;
    // pointerover repeats while moving inside the swatch; only its first entry counts.
    if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
    prefetchSwatch(swapDataCache, swatch);
  }

  // Pure: a touch that moved further than this was a scroll, not a tap.
  function getIsTap(startPoint, endPoint) {
    if (!startPoint || !endPoint) return false;
    return Math.abs(endPoint.x - startPoint.x) <= TAP_MOVE_TOLERANCE_PX &&
      Math.abs(endPoint.y - startPoint.y) <= TAP_MOVE_TOLERANCE_PX;
  }

  function getTouchPoint(touch) {
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  // iPad Safari treats the first tap as a hover whenever a :hover rule changes what is
  // shown (the card's hover photo swap does), and only sends the click on a second tap.
  // Swapping on touchend makes one tap enough; preventDefault stops the follow-up click.
  function createTouchTapHandlers(swapDataCache) {
    var tapStart = { swatch: null, point: null };
    return {
      handleTouchStart: function (event) {
        var swatch = getSwatchFromEvent(event);
        var isSingleTouch = event.touches && event.touches.length === 1;
        tapStart = { swatch: isSingleTouch ? swatch : null, point: isSingleTouch ? getTouchPoint(event.touches[0]) : null };
      },
      handleTouchEnd: function (event) {
        var startedSwatch = tapStart.swatch;
        var startPoint = tapStart.point;
        tapStart = { swatch: null, point: null };
        if (!startedSwatch || getSwatchFromEvent(event) !== startedSwatch) return;
        var endPoint = getTouchPoint(event.changedTouches && event.changedTouches[0]);
        if (!getIsTap(startPoint, endPoint)) return;
        if (startSwatchSwap(swapDataCache, startedSwatch) && event.cancelable) event.preventDefault();
      }
    };
  }

  function handleSwatchClick(swapDataCache, event) {
    var swatch = getSwatchFromEvent(event);
    if (!swatch || !getIsPlainPrimaryClick(event)) return;
    if (startSwatchSwap(swapDataCache, swatch)) event.preventDefault();
  }

  // Returns true when the swatch is handled in place (the link must not be followed).
  function startSwatchSwap(swapDataCache, swatch) {
    var swapTarget = getSwapTarget(swatch);
    if (!swapTarget) {
      console.warn(LOG_PREFIX, 'Swatch is missing its card or product URL; following the link instead.', swatch);
      return false;
    }
    if (swatch.getAttribute('aria-current') === 'true') return true;

    var card = swapTarget.card;
    var swapDataUrl = swapTarget.swapDataUrl;
    setCardSwapping(card, swapDataUrl);
    // Show the chosen colour straight away; the rest of the card follows once loaded.
    applyActiveSwatch(card, swatch);

    swapDataCache.get(swapDataUrl)
      .then(function (swapData) {
        // A later swatch click on the same card wins.
        if (card.getAttribute(PENDING_SWAP_ATTRIBUTE) !== swapDataUrl) return;
        card.setAttribute(CURRENT_SWAP_ATTRIBUTE, swapDataUrl);
        applySwapDetails(card, swatch, swapData);
        clearCardSwapping(card);
        return swapImagesWhenReady(card, swapDataUrl, swapData);
      })
      .catch(function (error) {
        if (card.getAttribute(PENDING_SWAP_ATTRIBUTE) !== swapDataUrl) {
          console.error(LOG_PREFIX, 'Card swap step failed after a newer swap or after the details changed.', error);
          return;
        }
        clearCardSwapping(card);
        console.error(LOG_PREFIX, 'Could not swap the card; opening the product page instead.', error);
        document.location.assign(swatch.href);
      });
    return true;
  }

  function initProductCardSwatchSwap() {
    try {
      var swapDataCache = createSwapDataCache();
      var handleIntent = function (event) { handleSwatchIntent(swapDataCache, event); };
      document.addEventListener('pointerover', handleIntent);
      document.addEventListener('focusin', handleIntent);
      document.addEventListener('pointerdown', function (event) { handleSwatchPress(swapDataCache, event); });
      document.addEventListener('click', function (event) { handleSwatchClick(swapDataCache, event); });
      var touchTapHandlers = createTouchTapHandlers(swapDataCache);
      document.addEventListener('touchstart', touchTapHandlers.handleTouchStart, { passive: true });
      document.addEventListener('touchend', touchTapHandlers.handleTouchEnd, { passive: false });
    } catch (error) {
      console.error(LOG_PREFIX, 'Initialisation failed; swatches will act as plain links.', error);
    }
  }

  initProductCardSwatchSwap();
})();
